import { useEffect, useMemo, useState } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { ClaimStudioInstance } from '@/api/models/studioInstance';
import { subscriptionApi } from '@/api/subscription';
import { TxStepId, TxStepStatus } from '@/common/types';
import { useFooterPadding } from '@/hooks/useFooterPadding';
import {
  initializePrimarySteps,
  initializeSecondarySteps,
} from '@/lib/transactionsFlow/factories/stepFactory';
import { moveToStep } from '@/lib/transactionsFlow/utils';
import useMainStore from '@/store/mainStore';
import CallToActionUX from './CallToActionUX';
import CheckoutFlow from './CheckoutFlow';
import CheckoutSecondaryFlow from './CheckoutSecondaryFlow';
import CreateYourOwnButton from './CreateYourOwnButton';
import Creator from './Creator';
import CrossmintTransactionsFlow from './CrossmintTransactionsFlow';
import Description from './Description';
import InfoSections from './InfoSections';
import Modal from './Modal';
import Name from './Name';
import RedeemMedia from './RedeemMedia';
import SideColumnModal from './SideColumnModal';
import SuccessComponent from './SuccessComponent';
import TransactionsFlow from './TransactionsFlow';
import YoutubeEmbed from './YoutubeEmbed';

export default function ClaimLayout() {
  // main store state and actions
  const setTxSteps = useMainStore((state) => state.setTxSteps);
  const store = useMainStore();
  const studioInstance = useMainStore((state) => state.studioInstance);
  const walletAddress = useMainStore((state) => state.walletAddress);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const collectorEmail = useMainStore((state) => state.collectorEmail);
  const txSteps = useMainStore((state) => state.txSteps);
  const resetStoreAfterPurchase = useMainStore((state) => state.resetStoreAfterPurchase);

  // component state
  const [oldWalletAddress, setOldWalletAddress] = useState(walletAddress);
  const [showCheckoutFlow, setShowCheckoutFlow] = useState(false);
  const [showTransactionsFlow, setShowTransactionsFlow] = useState(false);
  const [showSuccessFlow, setShowSuccessFlow] = useState(false);
  const [showCrossmintCheckoutFlow, setShowCrossmintCheckoutFlow] = useState(false);
  const [showSecondaryCheckoutFlow, setShowSecondaryCheckoutFlow] = useState(false);

  // @dev: used to disable the close button on the transactions flow while a tx is confirming
  const hasConfirmingTransaction = txSteps.some(
    (step) => step.state.status === TxStepStatus.Confirming,
  );

  const connectStepIndex = useMemo(() => {
    return store.txSteps.findIndex((step) => step.config.id === TxStepId.Connect);
  }, [store.txSteps]);

  // Use the custom hook to adjust footer padding on mobile to account for the sticky bottom action button
  useFooterPadding();

  const closeAllModalsAndFlows = () => {
    setShowCheckoutFlow(false);
    setShowTransactionsFlow(false);
    setShowSuccessFlow(false);
    setShowCrossmintCheckoutFlow(false);
    setShowSecondaryCheckoutFlow(false);
  };

  const onCheckoutClose = () => {
    closeAllModalsAndFlows();
    resetStoreAfterPurchase();
  };

  const onSecondaryCheckoutClose = () => {
    closeAllModalsAndFlows();
    resetStoreAfterPurchase();
  };

  const onConfirmCryptoCheckout = async () => {
    closeAllModalsAndFlows();
    setShowTransactionsFlow(true);

    if (!studioInstance) {
      throw new Error('Studio instance is undefined');
    }

    if (collectorEmail) {
      if (studioInstance.creator.address) {
        await subscriptionApi.subscribeToCreator(studioInstance.creator.address, collectorEmail);
      }
    }

    await initializePrimarySteps(store, studioInstance);
    /**
     * @important UX improvement
     * @dev skips the connect step and start with the next step if they
     * have already connected their wallet when we open TransactionsFlow
     */
    if (walletAddress) {
      const connectIndex = store.txSteps.findIndex((s) => s.config.id === TxStepId.Connect);
      moveToStep(store, connectIndex + 1);
    }
  };

  const onConfirmCardCheckout = () => {
    closeAllModalsAndFlows();
    setShowCrossmintCheckoutFlow(true);
  };

  const onConfirmSecondaryCryptoCheckout = async () => {
    closeAllModalsAndFlows();
    setShowTransactionsFlow(true);

    if (!studioInstance) {
      throw new Error('Studio instance is undefined');
    }

    if (!numberOfMints) {
      throw new Error('Number of tokens to redeem is undefined');
    }

    if (collectorEmail) {
      if (studioInstance.creator.address) {
        await subscriptionApi.subscribeToCreator(studioInstance.creator.address, collectorEmail);
      }
    }

    await initializeSecondarySteps(store, studioInstance);
    // if we have a wallet address, skip the connect step
    if (walletAddress && connectStepIndex !== -1) {
      moveToStep(store, connectStepIndex + 1);
    }
  };

  const openCheckoutFlow = async () => {
    if (!studioInstance) {
      throw new Error('Studio instance is undefined');
    }
    closeAllModalsAndFlows();
    setShowCheckoutFlow(true);
  };

  const openSecondaryCheckoutFlow = async () => {
    if (!studioInstance) {
      throw new Error('Studio instance is undefined');
    }
    closeAllModalsAndFlows();
    setShowSecondaryCheckoutFlow(true);
  };

  const onTransactionsSuccess = async () => {
    closeAllModalsAndFlows();
    setShowSuccessFlow(true);
  };

  // This handles wallet address changes, specifically logging people out if they swap wallets
  useEffect(
    () => {
      // this handles where the user logs out and we no longer have a wallet address
      if (oldWalletAddress && !walletAddress) {
        // reset tx steps
        setTxSteps(
          txSteps.map((step, index) => {
            if (index === 0) {
              return { ...step, status: TxStepStatus.Pending };
            } else {
              return { ...step, status: TxStepStatus.Unstarted };
            }
          }),
        );
      }

      setOldWalletAddress(walletAddress);
    },
    // purposely only ran when the internal wallet address changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [walletAddress],
  );

  /**
   * @dev: Handle scroll overflow for the left column. That way after you scroll
   * to the bottom of the left column, you scroll down to see the rest of the widget
   * naturally. If you scroll up, it will scroll up naturally to see the rest
   * of the widget.
   */
  useEffect(() => {
    // Get the scrollable container
    const scrollContainer = document.querySelector('.m-claim--overflow-y-auto');
    if (!scrollContainer) return;

    // Function to check if device is mobile
    const isMobileDevice = () => {
      return (
        window.innerWidth < 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      );
    };

    // Function to handle wheel events
    const handleWheel = (e: Event) => {
      // Skip this behavior on mobile devices
      if (isMobileDevice()) return;

      const wheelEvent = e as WheelEvent;
      const container = e.currentTarget as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Use a small threshold (2px) to make the transition smoother
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 2;
      const isAtTop = scrollTop <= 2;

      // Check if we're at the bottom of the container and trying to scroll down
      if (isAtBottom && wheelEvent.deltaY > 0) {
        // Prevent the default behavior of this event
        e.preventDefault();

        // Instead of stopping propagation, we'll manually dispatch a new event to the window
        // This allows the page to scroll naturally when the container is at the bottom
        window.scrollBy({
          top: wheelEvent.deltaY,
        });
      }

      if (isAtTop && wheelEvent.deltaY < 0) {
        // Prevent the default behavior of this event
        e.preventDefault();

        // Instead of stopping propagation, we'll manually dispatch a new event to the window
        // This allows the page to scroll naturally when the container is at the bottom
        window.scrollBy({
          top: wheelEvent.deltaY,
        });
      }
    };

    // Add the event listener with the passive option set to false to allow preventDefault
    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    // Clean up
    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <SkeletonTheme
      // @dev: styles all <Skeleton /> components throughout the widget
      baseColor="#202020"
      highlightColor="#444"
    >
      <div
        className="m-claim--flex m-claim--h-full m-claim--flex-col m-claim--gap-8 m-claim--bg-surface-primary"
        style={{
          background: `url(${(studioInstance as ClaimStudioInstance)?.publicData.clientTheme?.mediaBackground?.image || ''})`,
          backgroundSize: 'fit',
          backgroundPosition: 'center',
        }}
      >
        {/* Main content section - height constrained only on desktop */}
        <div className="m-claim--size-full m-claim--overflow-hidden md:m-claim--h-[100dvh]">
          {/* Grid container with padding */}
          <div className="m-claim--grid m-claim--h-full m-claim--grid-cols-1 m-claim--gap-8 m-claim--p-6 md:m-claim--grid-cols-[400px_1fr]">
            {/* Left Column - Scrollable content */}
            <div className="m-claim--order-2 m-claim--flex m-claim--h-full m-claim--flex-col md:m-claim--order-1">
              <div className="m-claim--min-h-full m-claim--overflow-y-auto m-claim--pr-2">
                <div className="m-claim--flex  m-claim--min-h-full m-claim--flex-col m-claim--gap-6">
                  {/* Studio Instance Name */}
                  <Name />

                  {/* Creator section aka Identity Widget */}
                  <Creator />

                  {/* Description */}
                  <Description />

                  {/* Video Embed */}
                  <YoutubeEmbed />

                  {/* Handles connect wallet and opening the checkout system as well as displaying the countdown and mint price */}
                  <div className="m-claim--hidden md:m-claim--block">
                    <CallToActionUX
                      onAction={openCheckoutFlow}
                      onSecondaryAction={openSecondaryCheckoutFlow}
                      variant="desktop"
                    />
                  </div>

                  {/* Expandable sections */}
                  <InfoSections />

                  {/* Make Your Own button */}
                  <CreateYourOwnButton />

                  {/* Add extra padding at the bottom on mobile for the sticky button */}
                  <div className="m-claim--h-32 md:m-claim--hidden"></div>
                </div>
              </div>
            </div>

            {/* Right Column - NFT Media */}
            <div className="m-claim--order-1 m-claim--h-full md:m-claim--order-2">
              <div className="m-claim--aspect-square m-claim--size-full">
                <RedeemMedia />
                {(studioInstance as ClaimStudioInstance)?.publicData.clientTheme?.logos?.main && (
                  <div className="m-claim--absolute m-claim--right-0 m-claim--top-0">
                    {/* Custom optional logo */}
                    <img
                      src={
                        (studioInstance as ClaimStudioInstance)?.publicData.clientTheme?.logos?.main
                      }
                      alt="Logo"
                      width={64}
                      height={64}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Action Button */}
        <div className="m-claim--fixed m-claim--inset-x-0 m-claim--bottom-0 m-claim--z-40 m-claim--backdrop-blur-sm md:m-claim--hidden">
          <CallToActionUX
            onAction={openCheckoutFlow}
            onSecondaryAction={openSecondaryCheckoutFlow}
            variant="mobile"
          />
        </div>

        {/* Checkout Flow */}
        <SideColumnModal
          isOpen={showCheckoutFlow}
          needsDefinedHeightContainer={true}
          onClose={onCheckoutClose}
        >
          <CheckoutFlow
            onConfirmCryptoCheckout={onConfirmCryptoCheckout}
            onConfirmCardCheckout={onConfirmCardCheckout}
          />
        </SideColumnModal>

        {/* Secondary Checkout Flow */}
        <SideColumnModal
          isOpen={showSecondaryCheckoutFlow}
          needsDefinedHeightContainer={true}
          onClose={onSecondaryCheckoutClose}
        >
          <CheckoutSecondaryFlow onConfirmCryptoCheckout={onConfirmSecondaryCryptoCheckout} />
        </SideColumnModal>

        {/* Transactions Flow */}
        <SideColumnModal
          isOpen={showTransactionsFlow}
          needsDefinedHeightContainer={true}
          onClose={() => {
            setShowTransactionsFlow(false);
            setShowCheckoutFlow(false);
            setShowCrossmintCheckoutFlow(false);
            setShowSecondaryCheckoutFlow(false);
            // Reset flow config when closing
            setTxSteps([]);
          }}
          // shows a bespoke close button that disappears during pending tx
          disableCloseButton={hasConfirmingTransaction}
        >
          <TransactionsFlow onSuccess={onTransactionsSuccess} />
        </SideColumnModal>

        {/* Crossmint Transactions Flow */}
        <SideColumnModal
          isOpen={showCrossmintCheckoutFlow}
          needsDefinedHeightContainer={true}
          onClose={closeAllModalsAndFlows}
          hideCloseButton={true}
          // shows a bespoke close button that disables during pending tx
        >
          <CrossmintTransactionsFlow
            onSuccess={onTransactionsSuccess}
            onClose={closeAllModalsAndFlows}
          />
        </SideColumnModal>

        {/* Success Modal */}
        <Modal
          isOpen={showSuccessFlow}
          onClose={() => {
            // hide other flows flows
            closeAllModalsAndFlows();

            // Reset the state so that the user can start over
            resetStoreAfterPurchase();
          }}
        >
          <SuccessComponent
            awaitingConfirmation={false}
            totalMinted={numberOfMints}
          />
        </Modal>
      </div>
    </SkeletonTheme>
  );
}
